import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appAssistantApi, type AppAssistantProposal } from '@/api/app-assistant.api';
import { appointmentsApi, type CreateAppointmentInput } from '@/api/appointments.api';
import { tasksApi, type CreateTaskInput } from '@/api/tasks.api';
import { consentsApi } from '@/api/consents.api';
import { ApiError } from '@/api/client';
import { useCan } from './session';
import { useTranslation } from 'react-i18next';

/** Cheia cererii obișnuite pe care o face confirmarea. */
const NEEDS: Record<AppAssistantProposal['kind'], string | null> = {
  programare: 'programari.creeaza',
  sarcina: null,
  sms: 'sms.pacient',
};

const DONE: Record<AppAssistantProposal['kind'], string> = {
  programare: 'Programarea a fost făcută.',
  sarcina: 'Sarcina a fost adăugată în listă.',
  sms: 'SMS-ul a fost trimis prin clinică.',
};

/**
 * O acțiune pregătită de asistent (`docs/proiectare/AI-ASISTENT.md`, faza 3).
 *
 * Asistentul nu face nimic singur: „Confirmă” trimite cererea obișnuită —
 * aceeași ca din Calendar, din Centrul de comandă sau din fișă, cu permisiunea,
 * validarea și jurnalul ei — și abia apoi notează propunerea ca făcută.
 */
export function AssistantProposalCard({ proposal }: { proposal: AppAssistantProposal }) {
  const { t } = useTranslation('common');
  const qc = useQueryClient();
  const can = useCan();
  const [status, setStatus] = useState(proposal.status);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const needs = NEEDS[proposal.kind];
  const allowed = needs === null || can(needs);

  const confirm = useMutation({
    mutationFn: async (): Promise<string | null> => {
      const body = proposal.payload;
      if (proposal.kind === 'programare') {
        const made = await appointmentsApi.create(body as unknown as CreateAppointmentInput);
        return made[0]?.id ?? null;
      }
      if (proposal.kind === 'sarcina') {
        return (await tasksApi.create(body as unknown as CreateTaskInput)).id;
      }
      const sent = await consentsApi.sendSms(String(body.patientId), String(body.text));
      if (sent.status !== 'sent') {
        setNote(sent.reason ? `SMS-ul nu a plecat: ${sent.reason}` : 'SMS-ul nu a plecat.');
      }
      return null;
    },
    onMutate: () => setError(null),
    onSuccess: async (resultId) => {
      setStatus('done');
      if (proposal.kind === 'programare') void qc.invalidateQueries({ queryKey: ['appointments'] });
      if (proposal.kind === 'sarcina') void qc.invalidateQueries({ queryKey: ['tasks'] });
      if (proposal.id) {
        await appAssistantApi
          .decide(proposal.id, { status: 'done', ...(resultId ? { resultId } : {}) })
          .catch(() => undefined);
        void qc.invalidateQueries({ queryKey: ['app-assistant'] });
      }
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : t('actiuneaNuAPutut')),
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      if (proposal.id) await appAssistantApi.decide(proposal.id, { status: 'dismissed' });
    },
    onSuccess: () => {
      setStatus('dismissed');
      if (proposal.id) void qc.invalidateQueries({ queryKey: ['app-assistant'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : t('nuSAPutut2')),
  });

  return (
    <div className={`aa-card aa-card--${status}`} role="group" aria-label="Propunere">
      <p className="aa-card__summary">{proposal.summary}</p>
      {status === 'proposed' && proposal.warnings.length ? (
        <ul className="aa-card__warn">
          {proposal.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
      {status === 'proposed' ? (
        allowed ? (
          <div className="aa-card__actions">
            <button
              type="button"
              className="aa-card__ok"
              disabled={confirm.isPending || dismiss.isPending}
              onClick={() => confirm.mutate()}
            >
              {confirm.isPending ? t('seFace') : t('confirm')}
            </button>
            <button
              type="button"
              className="aa-card__no"
              disabled={confirm.isPending || dismiss.isPending}
              onClick={() => dismiss.mutate()}
            >
              {t('renunta')}
            </button>
          </div>
        ) : (
          <p className="aa-card__state">{t('rolulDumneavoastraNuPoate')}</p>
        )
      ) : (
        <p className="aa-card__state">
          {status === 'done' ? (note ?? DONE[proposal.kind]) : t('atiRenuntatLaPropunere')}
        </p>
      )}
      {error ? (
        <p className="aa-card__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
